// Archivio locale dei pezzi della registrazione non ancora arrivati al server (e dei momenti
// segnati), più lo stato della registrazione in corso: sopravvive alla chiusura della scheda e
// alla sospensione del PC. In IndexedDB (DB `unasca-verbali`, store `pezzi` e `stato`); se il
// browser non lo permette, in memoria (si perde chiudendo la pagina).

export interface PezzoInAttesa {
  /** `${audioId}:${ripresa}:${indice}` per i pezzi, `${audioId}:s:${creatoAt}` per i segnaposti. */
  chiave: string;
  eventId: string;
  audioId: string;
  tipo: 'pezzo' | 'segnaposto';
  ripresa: number;
  indice?: number;
  blob?: Blob;
  msRipresa?: number;
  etichetta?: string;
  creatoAt: number;
}

export interface StatoRegistrazioneLocale {
  audioId: string;
  ripresa: number;
  pezziCreati: number;
  msRipresa: number;
  iniziataAt: number;
  ultimoPezzoAt: number;
  inPausa: boolean;
}

export interface ArchivioPezzi {
  salva(p: PezzoInAttesa): Promise<void>;
  togli(chiave: string): Promise<void>;
  elenca(eventId: string): Promise<PezzoInAttesa[]>;
  salvaStato(eventId: string, s: StatoRegistrazioneLocale | null): Promise<void>;
  leggiStato(eventId: string): Promise<StatoRegistrazioneLocale | null>;
}

export const chiavePezzo = (audioId: string, ripresa: number, indice: number) => `${audioId}:${ripresa}:${indice}`;
export const chiaveSegnaposto = (audioId: string, creatoAt: number) => `${audioId}:s:${creatoAt}`;

/**
 * Ordine di invio: audio per primo pezzo creato, poi ripresa, poi i pezzi per indice e infine i
 * segnaposti per creazione (la ripresa esiste già sul server prima di qualunque segnaposto).
 */
export function ordinaPezzi(pezzi: PezzoInAttesa[]): PezzoInAttesa[] {
  const primoPerAudio = new Map<string, number>();
  for (const p of pezzi) {
    const prima = primoPerAudio.get(p.audioId);
    if (prima === undefined || p.creatoAt < prima) primoPerAudio.set(p.audioId, p.creatoAt);
  }
  const peso = (p: PezzoInAttesa) => (p.tipo === 'pezzo' ? 0 : 1);
  return [...pezzi].sort((a, b) =>
    (a.audioId === b.audioId ? 0 : (primoPerAudio.get(a.audioId)! - primoPerAudio.get(b.audioId)!) || a.audioId.localeCompare(b.audioId))
    || (a.ripresa - b.ripresa)
    || (peso(a) - peso(b))
    || (a.tipo === 'pezzo' ? (a.indice ?? 0) - (b.indice ?? 0) : 0)
    || (a.creatoAt - b.creatoAt)
    || a.chiave.localeCompare(b.chiave));
}

/** Archivio in memoria (ripiego senza IndexedDB e test). */
export function archivioInMemoria(): ArchivioPezzi {
  const pezzi = new Map<string, PezzoInAttesa>();
  const stati = new Map<string, StatoRegistrazioneLocale>();
  return {
    async salva(p) { pezzi.set(p.chiave, { ...p }); },
    async togli(chiave) { pezzi.delete(chiave); },
    async elenca(eventId) { return ordinaPezzi(Array.from(pezzi.values()).filter(p => p.eventId === eventId)); },
    async salvaStato(eventId, s) {
      if (s) stati.set(eventId, { ...s });
      else stati.delete(eventId);
    },
    async leggiStato(eventId) {
      const s = stati.get(eventId);
      return s ? { ...s } : null;
    },
  };
}

// ========== IndexedDB ==========

const NOME_DB = 'unasca-verbali';
const VERSIONE_DB = 1;
const STORE_PEZZI = 'pezzi';
const STORE_STATO = 'stato';
const CHIAVE_PROVA = '__prova__';
const ATTESA_APERTURA_MS = 5000;

/** Esegue `lavoro` in una transazione e risolve a transazione completata (dati scritti). */
function inTransazione<T>(
  db: IDBDatabase,
  store: string,
  modo: IDBTransactionMode,
  lavoro: (s: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, modo);
    let risultato: T | undefined;
    const r = lavoro(tx.objectStore(store));
    if (r) r.onsuccess = () => { risultato = r.result; };
    tx.oncomplete = () => resolve(risultato);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transazione annullata'));
  });
}

function apriDb(fabbrica: IDBFactory): Promise<IDBDatabase | null> {
  return new Promise(resolve => {
    let finito = false;
    const chiudi = (db: IDBDatabase | null) => {
      if (finito) { db?.close(); return; }
      finito = true;
      resolve(db);
    };
    const timer = setTimeout(() => chiudi(null), ATTESA_APERTURA_MS);
    let richiesta: IDBOpenDBRequest;
    try {
      richiesta = fabbrica.open(NOME_DB, VERSIONE_DB);
    } catch {
      clearTimeout(timer);
      chiudi(null);
      return;
    }
    richiesta.onupgradeneeded = () => {
      const db = richiesta.result;
      if (!db.objectStoreNames.contains(STORE_PEZZI)) {
        db.createObjectStore(STORE_PEZZI, { keyPath: 'chiave' }).createIndex('eventId', 'eventId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_STATO)) db.createObjectStore(STORE_STATO, { keyPath: 'eventId' });
    };
    richiesta.onsuccess = () => {
      clearTimeout(timer);
      const db = richiesta.result;
      db.onversionchange = () => db.close();
      chiudi(db);
    };
    richiesta.onerror = () => { clearTimeout(timer); chiudi(null); };
    richiesta.onblocked = () => { clearTimeout(timer); chiudi(null); };
  });
}

/**
 * Archivio IndexedDB; null se IndexedDB manca o non si può scrivere (navigazione privata,
 * spazio negato, Blob non memorizzabili: la prova scrive e toglie un piccolo Blob nello store
 * `pezzi` e uno stato nello store `stato`). Una connessione chiusa dal browser si riapre una
 * volta per operazione. `fabbrica` si passa solo nei test.
 */
export async function archivioIndexedDb(fabbricaDiProva?: IDBFactory): Promise<ArchivioPezzi | null> {
  const fabbrica: IDBFactory | undefined = fabbricaDiProva || (typeof indexedDB !== 'undefined' ? indexedDB : undefined);
  if (!fabbrica) return null;
  let db = await apriDb(fabbrica);
  if (!db) return null;
  try {
    const prova: PezzoInAttesa = {
      chiave: CHIAVE_PROVA, eventId: CHIAVE_PROVA, audioId: CHIAVE_PROVA, tipo: 'pezzo', ripresa: 0, indice: 0,
      blob: new Blob([new Uint8Array([0x55])], { type: 'application/octet-stream' }), creatoAt: Date.now(),
    };
    await inTransazione(db, STORE_PEZZI, 'readwrite', s => s.put(prova));
    await inTransazione(db, STORE_PEZZI, 'readwrite', s => s.delete(CHIAVE_PROVA));
    await inTransazione(db, STORE_STATO, 'readwrite', s => s.put({ eventId: CHIAVE_PROVA, stato: null }));
    await inTransazione(db, STORE_STATO, 'readwrite', s => s.delete(CHIAVE_PROVA));
  } catch {
    db.close();
    return null;
  }

  const conDb = async <T>(op: (d: IDBDatabase) => Promise<T>): Promise<T> => {
    try {
      return await op(db!);
    } catch (err: any) {
      if (err?.name !== 'InvalidStateError') throw err;
      const nuovo = await apriDb(fabbrica);
      if (!nuovo) throw err;
      db = nuovo;
      return op(db);
    }
  };

  return {
    async salva(p) {
      await conDb(d => inTransazione(d, STORE_PEZZI, 'readwrite', s => s.put(p)));
    },
    async togli(chiave) {
      await conDb(d => inTransazione(d, STORE_PEZZI, 'readwrite', s => s.delete(chiave)));
    },
    async elenca(eventId) {
      const pezzi = await conDb(d => inTransazione<PezzoInAttesa[]>(d, STORE_PEZZI, 'readonly',
        s => s.index('eventId').getAll(eventId) as IDBRequest<PezzoInAttesa[]>));
      return ordinaPezzi(pezzi || []);
    },
    async salvaStato(eventId, stato) {
      await conDb(d => inTransazione(d, STORE_STATO, 'readwrite', s => {
        if (stato) s.put({ eventId, stato });
        else s.delete(eventId);
      }));
    },
    async leggiStato(eventId) {
      const riga = await conDb(d => inTransazione<{ eventId: string; stato: StatoRegistrazioneLocale | null } | undefined>(
        d, STORE_STATO, 'readonly', s => s.get(eventId)));
      return riga?.stato || null;
    },
  };
}
